import type { LangCode } from "@/lib/types";

/**
 * Lets a route tell the root head() what its canonical URL and real locale set
 * actually are.
 *
 * __root.tsx emits canonical, og:url and every hreflang alternate for the whole
 * site, and it builds them by substituting the locale prefix into the REQUESTED
 * path. That is right for the ~40 hand-authored routes, whose path is identical
 * in all ten locales. It is wrong wherever the path is not: a /shop slug that
 * differs per locale, or a page that only has a body in Turkish. Both cases had
 * been handled by adding another regex to __root — the /privacy|/terms special
 * case is the surviving example — and every such special case is a second place
 * that can disagree with the sitemap. This is the one mechanism that replaces
 * them.
 *
 * Why a side channel rather than loaderData: root's head() receives a snapshot
 * of `matches` taken BEFORE the loaders run, so a leaf's loaderData reads as
 * undefined and its status as "pending" there. (The existing isNotFound check
 * only works because globalNotFound is set at match-creation time.) Heads do
 * run after all loaders in the same pass, so a value published by a loader is
 * readable by root's head — which is exactly what this is.
 */
export type CanonicalOverride = {
  /**
   * The de-localized, percent-ENCODED path this override was computed for.
   *
   * On a repeat load the router can skip the loader and run heads anyway, which
   * would otherwise let the previous page's override leak into this one's head.
   * readCanonicalOverride() returns nothing unless this matches the path being
   * rendered, so a stale value is inert rather than wrong.
   */
  contentPath: string;
  /** De-localized path for the ACTIVE locale — canonical and og:url. */
  selfPath: string;
  /**
   * De-localized path per locale, for hreflang.
   *
   * A locale absent from this map gets NO alternate emitted. That is the point:
   * the failure this replaces was emitting a guessed URL for all ten locales.
   * Omit the field entirely to keep the prefix-substitution default.
   */
  perLocale?: Partial<Record<LangCode, string>>;
};

type Store = { seo?: CanonicalOverride; notFoundPath?: string };

function serverStore(): Store | undefined {
  const storage = (globalThis as unknown as Record<string, unknown>).serverStorage as
    { getStore: () => Store | undefined } | undefined;
  return storage?.getStore();
}

/**
 * Browser fallback. The server must never use this — concurrent requests share
 * one module instance, so a value stored here would leak across visitors. On
 * the server the value lives in the per-request AsyncLocalStorage store, which
 * is what serverStore() reaches; the browser is single-threaded and navigates
 * one page at a time, so a module-level value is safe there.
 */
let clientOverride: CanonicalOverride | undefined;
let clientNotFoundPath: string | undefined;

export function publishCanonicalOverride(value: CanonicalOverride): void {
  const store = serverStore();
  if (store) {
    store.seo = value;
    return;
  }
  clientOverride = value;
}

/** The override, but only if it was computed for this exact path. */
export function readCanonicalOverride(contentPath: string): CanonicalOverride | undefined {
  const value = serverStore()?.seo ?? clientOverride;
  return value?.contentPath === contentPath ? value : undefined;
}

/**
 * Tell root's head() that this path is a 404, before throwing notFound().
 *
 * root's head() receives `matches` snapshotted BEFORE the loaders run, so a
 * loader-thrown notFound() reads there as status "pending": `isNotFound` was
 * false and the page shipped a self-referencing canonical, ten hreflang
 * alternates and no robots noindex on top of a 404 response — plus whatever
 * title getRootPageSeo() maps the first path segment to, so /events/<unknown>
 * was titled after the Future Fish Eurasia stand page. Only the unmatched-route
 * case worked, because globalNotFound is set at match-creation time and is
 * therefore already in the snapshot.
 *
 * Every `throw notFound()` in a loader must be preceded by this call. The
 * matching read below is what restores the isPrivate guard for those routes.
 */
export function publishNotFound(contentPath: string): void {
  const store = serverStore();
  if (store) {
    store.notFoundPath = contentPath;
    return;
  }
  clientNotFoundPath = contentPath;
}

/** Whether a loader declared THIS path a 404. Path-guarded for the same reason
 *  the override is: the router can re-run heads without re-running loaders, and
 *  a stale flag would mark a live page noindex. */
export function readNotFound(contentPath: string): boolean {
  return (serverStore()?.notFoundPath ?? clientNotFoundPath) === contentPath;
}

/** Test seam — the browser fallback is module state and must be resettable. */
export function __resetCanonicalOverrideForTests(): void {
  clientOverride = undefined;
  clientNotFoundPath = undefined;
}
